import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, MapPin } from "lucide-react";
import PageBanner from "../components/PageBanner";

const TEAM = [
  {
    name: "Elaine Gregory-McKenzie",
    role: "Founder & Chief Executive Officer",
    email: "Emckenzie@tmbglobal.news",
    image:
      "https://customer-assets.emergentagent.com/job_tmb-foundation/artifacts/zfg684n6_Elaine.png",
  },
  {
    name: "Hasani Ali",
    role: "Chief Business Development Officer",
    email: "Hali@tmbglobal.news",
    image:
      "https://customer-assets.emergentagent.com/job_tmb-foundation/artifacts/z40niojd_Hasani.png",
  },
  {
    name: "Juliette Morris",
    role: "Marketing Director",
    email: "JMorris@tmbglobal.news",
    image:
      "https://customer-assets.emergentagent.com/job_tmb-foundation/artifacts/5iwuy3dj_Julie.png",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageBanner
        overline="About us"
        title="Rooted in Michigan. Reaching the world."
        subtitle="Founded in the United States and connected through a global network, TMB Global Foundation builds connected ecosystems — not isolated programs."
        image="https://images.pexels.com/photos/33621860/pexels-photo-33621860.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      {/* Story */}
      <section className="section">
        <div className="container-tmb grid md:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="overline mb-4">Our story</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05] mb-6">
              A foundation born in Michigan, with networks across the globe.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7 space-y-5 text-[#4A5D54] text-base md:text-lg leading-relaxed"
          >
            <p>
              TMB Global Foundation was founded in Michigan, USA, and has since grown
              into a cross-border movement with partnerships across the United States,
              Africa and the global diaspora. We operate at the intersection of
              healthcare, energy, food systems, education and digital infrastructure.
            </p>
            <p>
              Our work is centered on designing and implementing scalable solutions that
              strengthen communities and build long-term sustainability — moving people
              beyond temporary support toward resilience, economic participation and
              independence within a modern, connected global economy.
            </p>
            <p>
              By integrating innovation with community-based strategies, we create
              environments where individuals and families can truly thrive. Our offices
              in Detroit and Johannesburg anchor a network of governments, healthcare
              providers, energy developers, educational institutions, private-sector
              innovators and diaspora leaders working as one.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder spotlight */}
      <section className="section bg-[#F0EBE1]">
        <div className="container-tmb grid md:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5"
          >
            <div className="relative rounded-3xl overflow-hidden bg-[#1A2421] aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_tmb-foundation/artifacts/zfg684n6_Elaine.png"
                alt="Elaine Gregory-McKenzie"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="md:col-span-7"
          >
            <div className="overline mb-4">Founder & Visionary</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05] mb-6">
              Elaine Gregory-McKenzie
            </h2>
            <p className="text-[#4A5D54] text-base md:text-lg leading-relaxed mb-5">
              Elaine Gregory-McKenzie is the founder, owner and visionary behind TMB
              Global Foundation. Her conviction is simple and uncompromising: every
              community deserves connected access to the systems that make modern life
              possible — health, energy, food, education and trusted digital
              infrastructure.
            </p>
            <p className="text-[#4A5D54] text-base md:text-lg leading-relaxed mb-5">
              From her base in Michigan, Elaine has spent her career building bridges
              between under-resourced communities and the innovation, capital and policy
              ecosystems that can transform them. She envisions a future where solar-
              powered clinics, secure health records, sustainable food networks and
              future-ready schools are the standard — not the exception.
            </p>
            <p className="text-[#4A5D54] text-base md:text-lg leading-relaxed mb-6">
              Under her leadership, TMB Global Foundation has assembled a global
              coalition of governments, healthcare providers, energy developers,
              educators and diaspora leaders — proving that connected systems, designed
              with the communities they serve, create lasting change.
            </p>
            <a
              href="mailto:Emckenzie@tmbglobal.news"
              data-testid="founder-email"
              className="btn-ghost"
            >
              <Mail className="w-4 h-4" /> Emckenzie@tmbglobal.news
            </a>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="section">
        <div className="container-tmb">
          <div className="max-w-2xl mb-14">
            <div className="overline mb-4">Leadership</div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05]">
              The team building the network.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TEAM.map((m, i) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="card-tmb overflow-hidden"
                data-testid={`team-member-${i}`}
              >
                <div className="aspect-square bg-[#1A2421] overflow-hidden">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl text-[#1A2421] mb-1">
                    {m.name}
                  </h3>
                  <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#C85A3A] mb-4">
                    {m.role}
                  </div>
                  <a
                    href={`mailto:${m.email}`}
                    data-testid={`team-email-${i}`}
                    className="inline-flex items-center gap-2 text-sm text-[#4A5D54] hover:text-[#C85A3A]"
                  >
                    <Mail className="w-4 h-4" /> {m.email}
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Offices */}
      <section className="section bg-[#F0EBE1]">
        <div className="container-tmb grid md:grid-cols-2 gap-6">
          {[
            {
              city: "Detroit, USA",
              addr: ["TMB Global Network", "2722 Second Street, Ste. G-2", "Detroit, MI 48201"],
            },
            {
              city: "Sandton, South Africa",
              addr: ["155 West Street", "Sandton, Johannesburg", "Gauteng 2126"],
            },
          ].map((o) => (
            <div key={o.city} className="card-tmb p-10">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-[#C85A3A]" />
                <div className="overline">{o.city}</div>
              </div>
              <div className="font-display text-2xl md:text-3xl text-[#1A2421] leading-snug">
                {o.addr.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="container-tmb mt-12 text-center">
          <Link to="/contact" data-testid="about-contact-cta" className="btn-primary">
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
